import { useMemo, useState } from 'react';
import { TeeFormModal } from '@/components/ui/tee-form-modal';
import { TeeInput } from '@/components/ui/tee-input';
import { TeeTextArea } from '@/components/ui/tee-textarea';
import { TeeDropDownList } from '@/components/ui/tee-dropdown-list';
import {
  CATS,
  CAT_KEYS,
  blankForm,
  toLogDto,
  validateLogForm,
  type LogFormErrors,
  type LogFormState,
} from '../currentWeek.logForm';
import type { LogActivityDto } from '@/types/gamePlanTracker';

interface LogActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (dto: LogActivityDto) => Promise<void> | void;
  isSaving?: boolean;
  todayISO: string;
  planStartISO: string;
  isUpcoming: boolean;
}

const chip = (active: boolean) =>
  active
    ? 'px-3 py-1.5 rounded-[6px] border text-[14px] font-medium transition-colors bg-[rgba(12,61,45,0.10)] border-[var(--border-active)] text-[var(--color-forest)]'
    : 'px-3 py-1.5 rounded-[6px] border text-[14px] font-medium transition-colors bg-[var(--background-card)] border-[var(--border-default)] text-[var(--text-content-title)] hover:bg-[var(--background-card-hover)]';

export function LogActivityModal({
  isOpen,
  onClose,
  onSave,
  isSaving,
  todayISO,
  planStartISO,
  isUpcoming,
}: LogActivityModalProps) {
  const [form, setForm] = useState<LogFormState>(() => blankForm(todayISO));
  const [errors, setErrors] = useState<LogFormErrors>({});

  const cat = form.cat ? CATS[form.cat] : null;
  const set = (patch: Partial<LogFormState>) =>
    setForm((f) => ({ ...f, ...patch }));

  const errorCount = useMemo(() => Object.keys(errors).length, [errors]);

  const handlePrimary = async () => {
    const e = validateLogForm(form, { todayISO, planStartISO, isUpcoming });
    setErrors(e);
    if (Object.keys(e).length) return;
    await onSave(toLogDto(form));
    setForm(blankForm(todayISO));
    setErrors({});
    onClose();
  };

  const handleClose = () => {
    setForm(blankForm(todayISO));
    setErrors({});
    onClose();
  };

  return (
    <TeeFormModal
      isOpen={isOpen}
      title="Log activity"
      onClose={handleClose}
      onPrimaryAction={handlePrimary}
      primaryActionLabel="Save activity"
      secondaryActionLabel="Cancel"
      onSecondaryAction={handleClose}
      isLoading={isSaving}
      isLoadingLabel="Saving…"
      dismissOnBackdropClick={false}
      dismissOnEscape={false}
    >
      <div className="flex flex-col gap-4">
        {/* Category */}
        <div className="flex flex-col gap-1.5">
          <p className="text-[14px] font-medium text-[var(--text-content-title)]">
            Category
          </p>
          <div className="flex flex-wrap gap-2">
            {CAT_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => set({ cat: key, type: '', outcome: '' })}
                className={chip(form.cat === key)}
              >
                {CATS[key].label}
              </button>
            ))}
          </div>
          {errors.cat && (
            <p className="text-[13px] text-[var(--text-badge-red)]">{errors.cat}</p>
          )}
        </div>

        {cat && (
          <>
            <TeeDropDownList
              label={cat.typeLabel}
              options={cat.types.map((t) => ({ label: t, value: t }))}
              value={form.type}
              onChange={(v) => set({ type: String(v) })}
              placeholder="Choose one"
              hint={errors.type}
            />

            <TeeInput
              label="Date of contact"
              type="date"
              value={form.date}
              onChange={(e) => set({ date: e.target.value })}
              destructive={!!errors.date}
              hint={errors.date}
            />

            <TeeInput
              label={cat.orgLabel}
              placeholder={cat.orgPh}
              value={form.org}
              onChange={(e) => set({ org: e.target.value })}
              destructive={!!errors.org}
              hint={errors.org}
            />

            <TeeInput
              label={cat.nameLabel}
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              destructive={!!errors.name}
              hint={errors.name}
            />

            <TeeInput
              label={cat.titleLabel}
              placeholder={cat.titlePh}
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
            />

            <div className="grid grid-cols-2 gap-3">
              <TeeInput
                label="Phone"
                value={form.phone}
                onChange={(e) => set({ phone: e.target.value })}
                destructive={!!errors.phone}
                hint={errors.phone}
              />
              <TeeInput
                label="Email"
                value={form.email}
                onChange={(e) => set({ email: e.target.value })}
                destructive={!!errors.email}
                hint={errors.email}
              />
            </div>
            {errors.contact && (
              <p className="text-[13px] text-[var(--text-badge-red)]">
                {errors.contact}
              </p>
            )}
            {cat.reachHint && !errors.contact && (
              <p className="text-[13px] text-[var(--text-content-sub-title)]">
                {cat.reachHint}
              </p>
            )}

            {cat.isEvent && (
              <div className="grid grid-cols-3 gap-3">
                <TeeInput
                  label="Event date"
                  type="date"
                  value={form.eventDate}
                  onChange={(e) => set({ eventDate: e.target.value })}
                  destructive={!!errors.eventDate}
                  hint={errors.eventDate}
                />
                <TeeInput
                  label="Cost ($)"
                  type="number"
                  value={form.cost}
                  onChange={(e) => set({ cost: e.target.value })}
                  destructive={!!errors.cost}
                  hint={errors.cost}
                />
                <TeeInput
                  label="Leads captured"
                  type="number"
                  value={form.leads}
                  onChange={(e) => set({ leads: e.target.value })}
                  destructive={!!errors.leads}
                  hint={errors.leads}
                />
              </div>
            )}

            <TeeDropDownList
              label={cat.outcomeLabel}
              options={cat.outcomes.map((o) => ({ label: o, value: o }))}
              value={form.outcome}
              onChange={(v) => set({ outcome: String(v) })}
              placeholder="Choose one"
            />

            <TeeInput
              label="Next step"
              placeholder={cat.nextPh}
              value={form.nextStep}
              onChange={(e) => set({ nextStep: e.target.value })}
              destructive={!!errors.nextStep}
              hint={errors.nextStep}
            />

            <TeeInput
              label="Follow-up date (optional)"
              type="date"
              value={form.followUp}
              onChange={(e) => set({ followUp: e.target.value })}
              destructive={!!errors.followUp}
              hint={errors.followUp}
            />

            <TeeTextArea
              label="Notes"
              placeholder={cat.notesPh}
              value={form.notes}
              onChange={(e) => set({ notes: e.target.value })}
              destructive={!!errors.notes}
              hint={errors.notes}
            />
          </>
        )}

        {errorCount > 0 && (
          <p className="text-[13px] text-[var(--text-badge-red)]">
            {errorCount} {errorCount === 1 ? 'field needs' : 'fields need'} attention
          </p>
        )}
      </div>
    </TeeFormModal>
  );
}
